import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepicker } from '@angular/material/datepicker';
import { DocumentService, UserService } from '@app/shared/services';
import moment from 'moment';
import { Moment } from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AddApiRapidValueService } from '../../../add-api-rapid-value.service';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY',
  },
  display: {
    dateInput: 'YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

@AutoUnsubscribe()
@Component({
  selector: 'add-vehicle-basic-details-info',
  templateUrl: './add-vehicle-basic-details-info.component.html',
  styleUrl: './add-vehicle-basic-details-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideMomentDateAdapter(MY_FORMATS)],
})
export class AddVehicleBasicDetailsInfoComponent implements OnInit, OnDestroy {
  @Input() currentFormId;
  @Input() dialogRef;
  @Input() apiMotorValueList;
  @Input() stepNumber = 1;
  @Input() templateId = null;
  @Input() inlineComponent = false;

  @Output() updateVehicleDetailsInfo = new EventEmitter<any>();
  @Output() saveUpdateVehicleDetailsInfo = new EventEmitter<boolean>();
  @Output() updateStepNumber = new EventEmitter<number>();

  vehicleForm: UntypedFormGroup;
  vehicleDetailsForm: UntypedFormGroup;
  date = new FormControl();
  trimList = [];
  isFormValid = true;
  connection = 'noActive';
  isFormEditMode = false;
  isLoadingData = false;
  maxDate: Date;
  receivedGeneralInformationData;
  showCopyDataButton = false;
  subscription = new Subscription();
  conditionList = [
    {
      value: 'Outstanding',
      label: 'Outstanding',
    },
    {
      value: 'Clean',
      label: 'Clean',
    },
    {
      value: 'Average',
      label: 'Average',
    },
    {
      value: 'Rough',
      label: 'Rough',
    },
  ];
  payload;
  response;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private cd: ChangeDetectorRef,
    private toastr: ToastrService,
    private addApiRapidValueService: AddApiRapidValueService,
    private _documentService: DocumentService
  ) {
    const currentYear = new Date().getFullYear();
    this.maxDate = new Date(currentYear, 11, 31);
  }

  ngOnInit(): void {
    this.vehicleForm = this.formBuilder.group({
      year: ['', Validators.required],
      make: ['', Validators.required],
      model: ['', Validators.required],
    });

    this.vehicleDetailsForm = this.formBuilder.group({
      trim: ['', Validators.required],
      condition: ['Outstanding', Validators.required],
      type: ['Trade-In', Validators.required],
      vin: [''],
    });

    if (this.apiMotorValueList && this.apiMotorValueList.setting?.year) {
      this.connection = 'active';
      this.setUpEditFrom();
      this.isFormEditMode = true;
    }

    this.subscription.add(
      this.addApiRapidValueService.getPropertyDataFromGeneral(this.templateId).subscribe((data) => {
        this.receivedGeneralInformationData = data;
        const propertyDataExist =
          data && Object.keys(data).length > 0 && Object.values(data).some((value) => value !== null && value !== undefined && value !== '');
        this.showCopyDataButton = propertyDataExist ? true : false;
      })
    );
  }

  setUpEditFrom() {
    this.vehicleForm.get('year').setValue(this.apiMotorValueList.setting.year);
    this.vehicleForm.get('make').setValue(this.apiMotorValueList.setting.make);
    this.vehicleForm.get('model').setValue(this.apiMotorValueList.setting.model);
    this.vehicleDetailsForm.get('trim').setValue(this.apiMotorValueList.setting.trim);
    this.vehicleDetailsForm.get('condition').setValue(this.apiMotorValueList.setting.condition);
  }

  updateVehicleDetails() {
    this.updateVehicleDetailsInfo.emit(true);
    this.isFormEditMode = false;
  }

  onValueChange(newValue: string) {
    if (newValue.trim()) {
      this.isFormValid = true;
      this.cd.detectChanges();
    }
  }

  onYearSelected(normalizedYear: Moment, datepicker: MatDatepicker<any>) {
    this.isFormValid = true;
    const ctrlValue = this.vehicleForm.controls.year.value ? this.vehicleForm.controls.year.value : moment();
    ctrlValue.year(normalizedYear.year());
    this.vehicleForm.get('year').setValue(ctrlValue);
    datepicker.close();
    this.cd.detectChanges();
  }

  getVehicleData() {
    this.isLoadingData = true;
    this.cd.detectChanges();
    const data = {
      year: new Date(this.vehicleForm.controls.year.value).getFullYear(),
      model: this.vehicleForm.controls.model.value.trim(),
      make: this.vehicleForm.controls.make.value.trim(),
    };
    this.subscription.add(
      this.userService.getCarDetails(data).subscribe((res) => {
        if (res.status == 'error') {
          this.isLoadingData = false;
          this.isFormValid = false;
          this.cd.detectChanges();
          return;
        }
        this.payload = data;
        this.response = res;
        try {
          let trimList = res.data.market_value.market_value_data.map((x) => x.trim);
          trimList.forEach((element, index) => {
            if (index == 0) {
              this.vehicleDetailsForm.controls.trim.setValue(element);
            }
            this.trimList.push({
              value: element,
              label: element,
            });
          });
          this.isLoadingData = false;
          this.isFormValid = true;
          this.updateStepNumber.emit(2);
          this.cd.detectChanges();
        } catch (error) {
          console.error('🚀 ~ AddVehicleBasicDetailsInfoComponent ~ this.userService.getCarDetails ~ error:', error);
          this.isFormValid = false;
          this.isLoadingData = false;
        }
      })
    );
  }

  setEstimateValue(response, setting) {
    try {
      let trim = response.data.market_value.market_value_data.find((x) => x.trim == setting.trim);
      return trim['market value'].find((x) => x.Condition == setting.condition)[setting.type];
    } catch (error) {
      return '$0';
    }
  }

  saveData() {
    let setting = { ...this.vehicleDetailsForm.value, ...this.vehicleForm.value };
    try {
      setting.estimateValue = this.setEstimateValue(this.response, setting);
      setting.year = setting.year.years();
      setting.isVehicleDetails = true;
    } catch (error) {}
    const data = {
      vault_user_form_id: this.currentFormId,
      type: 'vehicleDatabase',
      status: 'connected',
      response: JSON.stringify(this.response),
      setting: JSON.stringify(setting),
      payload: JSON.stringify(this.payload),
    };

    this.subscription.add(
      this._documentService.createVaultFormRapidIntegration(data).subscribe((res) => {
        this.connection = 'active';
        let element = data;
        element.payload = JSON.parse(data.payload);
        element.response = JSON.parse(data.response);
        element.setting = JSON.parse(data.setting);
        this.addApiRapidValueService.setPropertyDataFromLiveFeeds(element, this.templateId);
        this.cd.detectChanges();
        try {
          this.dialogRef.close({
            success: true,
            connectionStatus: 'connected',
          });
        } catch (error) {}
        this.saveUpdateVehicleDetailsInfo.emit(true);

        this.cd.detectChanges();
      })
    );
  }

  copyDataFromGeneralInfo() {
    if (this.receivedGeneralInformationData) {
      const data = this.receivedGeneralInformationData;
      const mappedData: any = {};
      Object.keys(data).forEach((controlName) => {
        const fieldName = controlName.toLowerCase();
        if (fieldName.includes('make')) {
          mappedData.make = data[controlName] || '';
        } else if (fieldName.includes('model')) {
          mappedData.model = data[controlName] || '';
        } else if (fieldName.includes('year')) {
          const makeYear = moment().year(Number(data.automobileYear));
          mappedData.year = makeYear || '';
        }
      });
      this.vehicleForm.patchValue(mappedData);
      Object.keys(mappedData).forEach((controlName) => {
        this.vehicleForm.controls[controlName].markAsDirty();
      });
      this.toastr.success(`Data from general information was successfully copied to Vehicle Database`);
      this.cd.detectChanges();
    }
  }

  disconnectEstablishedConnection() {
    this.updateVehicleDetailsInfo.emit({ disconnect: true });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  // fetchSave() {
  //   if (!this.isFetchedData) {
  //     this.isFetchingData = true;
  //     const data = {
  //       year: this.vehicleForm.controls.year.value,
  //       model: this.vehicleForm.controls.model.value,
  //       make: this.vehicleForm.controls.make.value,
  //     };
  //     this.subscription.add(
  //       this.userService.getCarDetails(data).subscribe((res) => {
  //         if (res.status == 'error') {
  //           this.isFetchingData = false;
  //           this.toastr.warning('sorry! we are not able to find vehicle');
  //           this.cd.detectChanges();
  //           return;
  //         }
  //         this.payload = data;
  //         this.response = res;
  //         this.isFetchedData = true;
  //         this.isFetchingData = false;
  //         this.cd.detectChanges();
  //         try {
  //           let trimList = res.data.market_value.market_value_data.map((x) => x.trim);
  //           trimList.forEach((element, index) => {
  //             if (index == 0) {
  //               this.vehicleForm.controls.trim.setValue(element);
  //             }
  //             this.trimList.push({
  //               value: element,
  //               label: element,
  //             });
  //           });
  //         } catch (error) {}
  //       })
  //     );
  //   } else {
  //     let setting = this.vehicleForm.value;
  //     try {
  //       setting.estimateValue = this.setEstimateValue(this.response, setting);
  //     } catch (error) {}
  //     const data = {
  //       vault_user_form_id: this.currentFormId,
  //       type: 'motor',
  //       status: 'connected',
  //       response: JSON.stringify(this.response),
  //       setting: JSON.stringify(setting),
  //       payload: JSON.stringify(this.payload),
  //     };

  //     this.subscription.add(
  //       this._documentService.createVaultFormMotorValutionIntegration(data).subscribe((res) => {
  //         this.dialogRef.close({
  //           success: true,
  //         });
  //         this.is_active_connection = true;
  //         this.cd.detectChanges();
  //       })
  //     );
  //   }
  // }
}
